import React from 'react';
import { SkeletonItem as SkeletonItemDI } from '@atlaskit/side-navigation';
import { isFedRamp } from '@atlassian/atl-context';
import { SERVICE_DESK_PROJECT } from '@atlassian/jira-common-constants/src/project-types.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { useIntlV2 as useIntlDI } from '@atlassian/jira-intl/src/v2/use-intl.tsx';
import { MenuItem as MenuItemDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/menu-item/main.tsx';
import { matchUrlPathname } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/url-matchers/index.tsx';
import { getNextgenSettingsUrl } from '@atlassian/jira-project-settings-apps-common/src/urls.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';

import { PROJECT_SETTINGS_SHIFTER_WEIGHT } from '../../../constants.tsx';
import type { TMPSettingFeatures } from '../../../types/index.tsx';
import { VirtualAgent } from '../../virtual-agent/index.tsx';
import { VirtualAgentMenuItem } from '../../virtual-agent/main.tsx';
import messages from '../messages.tsx';

type ChannelsProps = {
	projectKey: string;
	features: TMPSettingFeatures | null;
};
export const Channels = ({ projectKey, features }: ChannelsProps) => {
	const { formatMessage } = useIntlDI();
	const baseSettingsUrl = getNextgenSettingsUrl(SERVICE_DESK_PROJECT, projectKey);
	const tenantContext = useTenantContext();
	const { appPermissions } = tenantContext;
	const { hasServiceDeskAccess: hasJSD } = appPermissions;
	const BackMenuItem = {
		href: baseSettingsUrl,
		title: formatMessage(messages.goToSettings),
	};

	if (features == null || projectKey == null) return <SkeletonItemDI />;

	return (
		<>
			<MenuItemDI
				analytics={{ itemId: 'portal-settings' }}
				href={`${baseSettingsUrl}/channels/portal-settings`}
				selectedOn={matchUrlPathname()}
			>
				{formatMessage(messages.portalSettings)}
			</MenuItemDI>
			<MenuItemDI
				analytics={{ itemId: 'email' }}
				href={`${baseSettingsUrl}/email`}
				selectedOn={matchUrlPathname()}
			>
				{formatMessage(messages.email)}
			</MenuItemDI>
			{features?.hasChat && (
				<>
					<MenuItemDI
						analytics={{ itemId: 'chat-settings' }}
						href={`${baseSettingsUrl}/chat`}
						selectedOn={matchUrlPathname()}
					>
						{formatMessage(messages.chat)}
					</MenuItemDI>
				</>
			)}
			<MenuItemDI
				analytics={{ itemId: 'widget-settings' }}
				href={`${baseSettingsUrl}/widget`}
				selectedOn={matchUrlPathname()}
			>
				{formatMessage(messages.widget)}
			</MenuItemDI>
			{hasJSD && (
				<MenuItemDI
					analytics={{ itemId: 'knowledgeBase' }}
					selectedOn={['project-settings-servicedesk-kb']}
					href={`${baseSettingsUrl}/confluence-kb`}
					shifter={{
						groupTitle: formatMessage(messages.title),
						context: projectKey,
						weight: PROJECT_SETTINGS_SHIFTER_WEIGHT,
					}}
				>
					{formatMessage(messages.knowledgeBase)}
				</MenuItemDI>
			)}
			{!isFedRamp() && (
				<JSErrorBoundary
					id="virtualAgent"
					packageName="jiraNavigationAppsSidebarServicedesk"
					teamName="navigation"
					fallback={() => <VirtualAgentMenuItem projectKey={projectKey} />}
				>
					<VirtualAgent
						projectKey={projectKey}
						overrides={{ BackMenuItem }}
						shifter={{
							groupTitle: formatMessage(messages.title),
							context: projectKey,
							weight: PROJECT_SETTINGS_SHIFTER_WEIGHT,
						}}
					/>
				</JSErrorBoundary>
			)}
			<MenuItemDI
				analytics={{ itemId: 'external-links' }}
				selectedOn={['project-settings-servicedesk-external-links']}
				href={`${baseSettingsUrl}/external-links`}
			>
				{formatMessage(messages.externalLinks)}
			</MenuItemDI>
		</>
	);
};
